import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { SEARCH_DEBOUNCE_MS } from '../../../core/constants/forms';
import LoadMore from '../../../common/components/LoadMore/LoadMore';
import { useFarmsInfinite } from '../contexts/farms';
import { useClientsList } from '../../clients/contexts/clients';
import SectionTabs from '../../navigation/section-tabs';
import { CLIENT_TABS } from '../../navigation/tabs';

import '../../../common/styles/catalog.css';

// Catálogo de fincas (api-reference/admin-farms.md). Lista filtrable por CLIENTE
// (owner_id, server-side), por nombre (q) y por estado. La respuesta de fincas solo
// trae `owner_id` (no el nombre del dueño) → se resuelve contra la lista de clientes.

const STATUS_LABEL = { 2: 'Activo', 1: 'Inactivo', 0: 'Eliminado' };

// `location` es JSON libre (objeto o string): se muestra si es texto o si trae
// claves comunes; cualquier otra forma cae a "—" (nunca [object Object]).
function fmtLocation(loc) {
    if (!loc) return null;
    if (typeof loc === 'string') return loc.trim() || null;
    if (typeof loc === 'object') {
        const parts = [loc.address, loc.city, loc.region, loc.state, loc.name]
            .filter((x) => typeof x === 'string' && x.trim());
        return parts.length ? parts.join(', ') : null;
    }
    return null;
}

export default function FarmsOverview() {
    const [params, setParams] = useSearchParams();
    const urlSearch = params.get('q') || '';
    const urlOwner  = params.get('owner_id') || '';
    const urlStatus = params.get('status') || '';

    const [search, setSearch] = useState(urlSearch);
    const [debounced, setDebounced] = useState(urlSearch);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [search]);
    useEffect(() => {
        const next = new URLSearchParams(params);
        if (debounced) next.set('q', debounced); else next.delete('q');
        setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced]);

    const setParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value); else next.delete(key);
        setParams(next, { replace: true });
    };
    const clearFilters = () => { setSearch(''); setParams(new URLSearchParams(), { replace: true }); };

    // Clientes (dueños): pueblan el filtro y resuelven owner_id → nombre.
    const { data: clientsData } = useClientsList({ limit: 500, sort: 'name' });
    const clients = useMemo(() => clientsData?.items ?? [], [clientsData]);
    const clientName = useMemo(() => {
        const m = new Map();
        clients.forEach((c) => m.set(c.id, c.name));
        return m;
    }, [clients]);

    const filters = useMemo(() => {
        const out = { sort: 'name' };
        if (debounced) out.q = debounced;
        if (urlOwner) out.owner_id = urlOwner;
        if (urlStatus) out.status = urlStatus;
        return out;
    }, [debounced, urlOwner, urlStatus]);

    const { data, isLoading, isFetching, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useFarmsInfinite(filters);
    const farms = useMemo(() => data?.pages?.flatMap(p => p.items ?? []) ?? [], [data]);
    const total = data?.pages?.[0]?.pagination?.total ?? farms.length;
    const hasFilters = !!(search || urlOwner || urlStatus);

    return (
        <div className="cat-overview">
            <SectionTabs tabs={CLIENT_TABS} />
            <header className="cat-overview__header">
                <div className="cat-overview__heading">
                    <h1 className="cat-overview__title">Fincas</h1>
                    <span className="cat-overview__count">{total}</span>
                </div>
            </header>

            <div className="cat-overview__toolbar">
                <div className="cat-overview__search">
                    <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre…" aria-label="Buscar fincas por nombre" />
                </div>

                <select className="cat-overview__filter" value={urlOwner} onChange={(e) => setParam('owner_id', e.target.value)} aria-label="Filtrar por cliente">
                    <option value="">Todos los clientes</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>

                <select className="cat-overview__filter" value={urlStatus} onChange={(e) => setParam('status', e.target.value)} aria-label="Filtrar por estado">
                    <option value="">Activos e inactivos</option>
                    <option value="2">Activo</option>
                    <option value="1">Inactivo</option>
                </select>

                {hasFilters && <button type="button" className="cat-overview__clear" onClick={clearFilters}>Limpiar</button>}
            </div>

            {error && <div className="cat-overview__error">{error.message || 'No se pudieron cargar las fincas'}</div>}

            {isLoading ? (
                <div className="cat-overview__state">Cargando fincas…</div>
            ) : !farms.length ? (
                <div className="cat-overview__state">
                    <p className="cat-overview__state-text">{hasFilters ? 'Ninguna finca coincide con el filtro.' : 'Aún no hay fincas.'}</p>
                </div>
            ) : (
                <div className={`cat-overview__card ${isFetching ? 'cat-overview__card--busy' : ''}`}>
                    <table className="cat-table">
                        <thead>
                            <tr>
                                <th>Nombre</th><th>Cliente</th><th>Tamaño</th><th>Ubicación</th><th>Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {farms.map((f) => {
                                const loc = fmtLocation(f.location);
                                const owner = clientName.get(f.owner_id);
                                return (
                                    <tr key={f.id} className="cat-table__row">
                                        <td>
                                            <Gate domain="kpis" action="read" fallback={f.name || `#${f.id}`}>
                                                <Link to={`/admin/farms/${f.id}`} className="cat-table__name" title="Ver estadísticas">{f.name || `#${f.id}`}</Link>
                                            </Gate>
                                        </td>
                                        <td>{owner ? (
                                            <Gate domain="clients" action="read" fallback={owner}>
                                                <Link to={`/admin/clients/${f.owner_id}`} className="cat-table__name">{owner}</Link>
                                            </Gate>
                                        ) : (f.owner_id ? <span className="cat-table__empty">#{f.owner_id}</span> : <span className="cat-table__empty">—</span>)}</td>
                                        <td>{f.size != null ? `${f.size} ha` : <span className="cat-table__empty">—</span>}</td>
                                        <td>{loc || <span className="cat-table__empty">—</span>}</td>
                                        <td><span className={`cat-badge cat-badge--${f.status}`}>{STATUS_LABEL[f.status] || '—'}</span></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {!isLoading && !error && farms.length > 0 && (
                <LoadMore
                    shown={farms.length}
                    total={total}
                    hasMore={!!hasNextPage}
                    loading={isFetchingNextPage}
                    onMore={() => fetchNextPage()}
                    noun="fincas"
                />
            )}
        </div>
    );
}
